import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Pause, Pencil, Play, Plus, ShieldCheck, X } from 'lucide-react';

import { cn } from '@/shared/libs';
import { Dialog } from '@/shared/ui';
import {
  CoverageStrip,
  getOfferAttributes,
  getOfferDisplayName,
  LegBlock,
  OFFER_LEG_KEYS,
  OFFER_STATUS_I18N_KEY,
  OfferGeneral,
} from '@entities/Offer';
import type { AvailabilityStatus, Offer } from '@psp-aggregator/sdk';

export interface OfferDetailsModalProps {
  offer: Offer;
  onClose: () => void;
  onCopy?: () => void;
  onEdit?: () => void;
  onSelect?: () => void;
  onToggleStatus?: () => void;
  providerName?: string;
  selected?: boolean;
}

export const OfferDetailsModal: FC<OfferDetailsModalProps> = ({
  offer,
  onClose,
  onCopy,
  onEdit,
  onSelect,
  onToggleStatus,
  providerName,
  selected = false,
}) => {
  const { t } = useTranslation();
  const status: AvailabilityStatus = offer.status ?? 'active';
  const attributes = getOfferAttributes(offer);
  const isPaused = status === 'paused';
  const initial = (providerName ?? getOfferDisplayName(offer))
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <Dialog className="max-w-2xl p-0 sm:p-0" width={720} onClose={onClose}>
      <div className="max-h-[min(78dvh,calc(90dvh-2rem))] overflow-y-auto overscroll-contain">
        <CoverageStrip offer={offer} />
        <div className="space-y-4 p-4 sm:p-6">
          <div className="flex items-start justify-between gap-3 pr-6">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">
                {getOfferDisplayName(offer)}
              </h2>
              {!!offer.alias && (
                <span className="mt-1.5 inline-flex rounded-md border border-indigo-500/20 bg-indigo-500/10 px-2 py-0.5 font-mono text-[11px] text-indigo-600 dark:text-indigo-300">
                  {offer.alias}
                </span>
              )}
              {!!providerName && (
                <div className="mt-2 flex items-center gap-1.5">
                  <span className="flex h-5 w-5 items-center justify-center rounded-md bg-indigo-500/15 text-[10px] font-bold text-indigo-600 dark:text-indigo-300">
                    {initial}
                  </span>
                  <span className="text-sm font-medium">{providerName}</span>
                </div>
              )}
            </div>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-medium',
                isPaused
                  ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                  : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
              )}>
              {t(OFFER_STATUS_I18N_KEY[status])}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {!!onEdit && (
              <button
                aria-label={t('buttons.edit')}
                className="cursor-pointer rounded-lg p-1.5 text-muted-foreground hover:bg-indigo-500/10 hover:text-indigo-500"
                type="button"
                onClick={onEdit}>
                <Pencil className="h-4 w-4" />
              </button>
            )}
            {!!onCopy && (
              <button
                aria-label={t('buttons.copy')}
                className="cursor-pointer rounded-lg p-1.5 text-muted-foreground hover:bg-indigo-500/10 hover:text-indigo-500"
                type="button"
                onClick={onCopy}>
                <Copy className="h-4 w-4" />
              </button>
            )}
            {!!onToggleStatus && (
              <button
                aria-label={isPaused ? t('buttons.resume') : t('buttons.pause')}
                className={cn(
                  'cursor-pointer rounded-lg p-1.5',
                  isPaused
                    ? 'text-emerald-500 hover:bg-emerald-500/10'
                    : 'text-amber-500 hover:bg-amber-500/10',
                )}
                type="button"
                onClick={onToggleStatus}>
                {isPaused ? (
                  <Play className="h-4 w-4" />
                ) : (
                  <Pause className="h-4 w-4" />
                )}
              </button>
            )}
          </div>

          <OfferGeneral legLayout="hidden" offer={offer} />

          <div className="space-y-3">
            {OFFER_LEG_KEYS.map((leg) => (
              <LegBlock
                key={leg}
                layout="panel"
                leg={leg}
                offer={offer}
                showCost
              />
            ))}
          </div>

          {!!attributes.details && (
            <div>
              <p className="mb-1 text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
                {t('pages.admin.offers.fields.details')}
              </p>
              <p className="text-sm whitespace-pre-wrap text-foreground/90">
                {attributes.details}
              </p>
            </div>
          )}

          {attributes.custom_fields.length > 0 && (
            <div className="space-y-2">
              {attributes.custom_fields.map((field) => (
                <div key={field.key}>
                  <p className="text-[10px] tracking-wider text-muted-foreground uppercase">
                    {field.key}
                  </p>
                  <p className="text-sm">{field.value}</p>
                </div>
              ))}
            </div>
          )}

          {!!attributes.guarantee_amount && (
            <div className="flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                {t('pages.admin.offers.fields.guarantee')}{' '}
                {attributes.guarantee_amount}
              </span>
            </div>
          )}

          {!!onSelect && !isPaused && (
            <button
              className={cn(
                'inline-flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl px-3.5 py-2.5 text-sm font-medium',
                selected
                  ? 'border border-indigo-500 text-indigo-600 dark:text-indigo-300'
                  : 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/20',
              )}
              type="button"
              onClick={onSelect}>
              {selected ? (
                <X className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {selected
                ? t('pages.admin.offers.added')
                : t('pages.admin.offers.add')}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

OfferDetailsModal.displayName = 'OfferDetailsModal';
